import { getLandscape } from './manage-api';

// Source families reported by c2-manager per cloud. Cosmetic only — `inferCloud`
// derives the cloud from the source-key prefix, so new `azure_*`/`gcp_*` keys do
// not need to be listed here. Kept for the well-known AWS keys that have no prefix.
const SOURCE_CLOUD_MAP = {
  rds: 'aws',
  ec2: 'aws',
  s3: 'aws',
  azure_sql: 'azure',
  azure_vm: 'azure',
  azure_storage: 'azure',
  azure_blob: 'azure', // legacy alias for azure_storage
  gcp_sql: 'gcp',
  gce_vm: 'gcp',
  gcs: 'gcp',
};

// Cross-cloud category buckets rendered in the Resource Manager UI. Category
// membership is DATA-DRIVEN (see `kindForSource`) so any current or future
// `<cloud>_<engine>` source key categorizes automatically without an edit here.
// The `sources` lists below are illustrative only — used for the cosmetic
// subtitle on each category card, not for categorization or counts.
const CATEGORIES = {
  rdbm: {
    label: 'RDBMs',
    sources: ['rds', 'azure_sql', 'azure_sql_mi', 'azure_postgres', 'azure_mysql', 'azure_oracle', 'azure_synapse', 'gcp_sql'],
  },
  files: {
    label: 'Files',
    sources: ['s3', 'azure_storage', 'gcs'],
  },
  servers: {
    label: 'Servers',
    sources: ['ec2', 'azure_vm', 'gce_vm'],
  },
};

const CATEGORY_ORDER = ['rdbm', 'files', 'servers'];

// Derive the category bucket from the source key (+ engine fallback) rather than
// a hardcoded per-key list. Compute → servers, object storage → files, everything
// else (anything carrying a DB engine) → rdbm. This is the single source of truth
// for categorization; a missing key here was what hid Azure DBs/storage on /manage.
const kindForSource = (source) => {
  const s = String(source || '').toLowerCase();
  if (/(^|_)vm$/.test(s) || s === 'ec2') return 'servers';
  if (s === 's3' || s === 'gcs' || s.includes('storage') || s.includes('blob')) return 'files';
  return 'rdbm';
};

// Back-compat static map of the well-known keys (some callers seed buckets from
// its keys). Categorization itself no longer depends on it.
const CATEGORY_OF_SOURCE = {};
Object.entries(CATEGORIES).forEach(([cat, meta]) => {
  meta.sources.forEach((s) => {
    CATEGORY_OF_SOURCE[s] = cat;
  });
});

const isCategory = (key) =>
  Object.prototype.hasOwnProperty.call(CATEGORIES, key);

const sourcesForCategory = (category) =>
  (CATEGORIES[category] && CATEGORIES[category].sources) || [];

const categoryForSource = (source) => kindForSource(source);

const filterByCategory = (sources = [], category) => {
  if (!category || !isCategory(category)) return sources;
  return sources.filter((s) => categoryForSource(s.source) === category);
};

const inferCloud = (source) => {
  if (!source) return 'aws';
  if (source.startsWith('azure')) return 'azure';
  if (source.startsWith('gcp') || source.startsWith('gce') || source === 'gcs') return 'gcp';
  return SOURCE_CLOUD_MAP[source] || 'aws';
};

// Defensive: make sure every source has a `cloud` field. Legacy `landscape.json`
// that predates the redesign omits `cloud`, in which case DataSource.effectiveCloud()
// on the server treats null as "aws" — mirror that here.
const normalizeSource = (source) => ({
  ...source,
  cloud: source.cloud || inferCloud(source.source),
});

const getManagedSources = async () => {
  const landscape = await getLandscape();
  const sources = Array.isArray(landscape && landscape.sources)
    ? landscape.sources.map(normalizeSource)
    : [];
  return {
    time_stamp: (landscape && landscape.time_stamp) || 0,
    sources,
    errors: (landscape && landscape.errors) || [],
  };
};

const filterByProvider = (sources = [], providerFilter) => {
  if (!providerFilter || providerFilter === 'all') return sources;
  return sources.filter((s) => (s.cloud || 'aws') === providerFilter);
};

const filterByService = (sources = [], source) => {
  if (!source) return sources;
  return sources.filter((s) => s.source === source);
};

// Bucket rows by both individual source (legacy key) and by category.
const getRegionsMap = (managedSources = []) => {
  const map = {};
  CATEGORY_ORDER.forEach((cat) => (map[cat] = []));

  managedSources.forEach((data) => {
    const source = data.source;
    if (!map[source]) map[source] = [];
    map[source].push(data);

    const cat = categoryForSource(source);
    if (cat && map[cat]) map[cat].push(data);
  });
  return map;
};

const getCategoriesWithData = (regionsMap = {}) =>
  CATEGORY_ORDER.filter((cat) => regionsMap[cat] && regionsMap[cat].length > 0);

// --- Port-exposure reachability helpers -----------------------------------------------------
// c2-manager now tags each PortExposure with an explicit `internal` flag (network-scoped source:
// a security-group/ASG reference or a CIDR inside the resource's own VPC/VNet/subnet) plus the
// matched `internalNetwork` CIDR, and — for a DB port merely swept up inside a wide allow-range —
// `viaRange`/`rangeFrom`/`rangeTo`. Prefer these explicit fields; fall back to the legacy
// open-range string parse only for pre-upgrade payloads that predate the flag (field absent).

const isOpenRange = (range) => range === '0.0.0.0/0' || range === '::/0';

// True only when the scanner explicitly classified the source as network-scoped. A missing
// `internal` (pre-upgrade data) is "unknown", never hidden.
const isInternalExposure = (exp) => !!exp && exp.internal === true;

// Reachable from outside the resource's own network. Explicit `internal === false` wins; when the
// field is absent (legacy), fall back to the open-to-the-world source-range heuristic.
const isExternalExposure = (exp) => {
  if (!exp) return false;
  if (exp.internal === false) return true;
  if (exp.internal == null) return isOpenRange(exp.sourceRange);
  return false;
};

// The DB port was not individually opened — it fell inside a wide/unbounded allow-range.
const isViaRangeExposure = (exp) => !!exp && exp.viaRange === true;

// Loud "open to the internet" treatment is reserved for externally-reachable ports that a rule
// deliberately opened — never for a port merely caught inside a broad range.
const isLoudExposure = (exp) => isExternalExposure(exp) && !isViaRangeExposure(exp);

// e.g. "within 1025-65535" for a range-derived DB port; null when not range-derived.
const rangeProvenanceLabel = (exp) =>
  isViaRangeExposure(exp) && exp.rangeFrom != null && exp.rangeTo != null
    ? `within ${exp.rangeFrom}-${exp.rangeTo}`
    : null;

// --- Port-exposure ordering + labels --------------------------------------------------------
// Most-likely-a-database first. Newer managers already return exposures in this order; sorting
// client-side keeps older payloads (and any caller-side regrouping) consistent.
const PORT_MATCH_RANK = { exact: 0, nearby: 1, non_standard: 2 };

const portMatchRank = (match) => {
  const key = String(match || '').toLowerCase();
  return Object.prototype.hasOwnProperty.call(PORT_MATCH_RANK, key) ? PORT_MATCH_RANK[key] : 3;
};

// Stable copy-sort: exact → nearby → non_standard, then port ascending within each band.
const sortPortExposures = (exposures = []) =>
  [...(exposures || [])].sort(
    (a, b) => portMatchRank(a && a.match) - portMatchRank(b && b.match)
      || ((a && a.port) || 0) - ((b && b.port) || 0)
  );

// "5432", or "1024–1100" for a collapsed range row.
const portRangeLabel = (exp = {}) =>
  exp.portEnd != null && exp.portEnd !== exp.port
    ? `${exp.port}–${exp.portEnd}`
    : String(exp.port);

// --- "Hide internal-network sources" view preference ---------------------------------------
// Persisted like other view prefs in this app (direct localStorage, e.g. `lastConnection`).
// Client-side only: components always render from the full payload and filter on read, so
// toggling needs no re-scan. Default OFF (show everything).
const HIDE_INTERNAL_PREF_KEY = 'assetManager.hideInternalSources';

const getHideInternalPref = () => {
  try {
    return localStorage.getItem(HIDE_INTERNAL_PREF_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const setHideInternalPref = (value) => {
  try {
    localStorage.setItem(HIDE_INTERNAL_PREF_KEY, value ? '1' : '0');
  } catch (e) {
    // Ignore storage errors (private mode / quota) — the toggle still works in-session.
  }
};

export {
  getManagedSources,
  filterByService,
  filterByProvider,
  filterByCategory,
  getRegionsMap,
  getCategoriesWithData,
  inferCloud,
  normalizeSource,
  categoryForSource,
  kindForSource,
  sourcesForCategory,
  isCategory,
  CATEGORIES,
  CATEGORY_ORDER,
  CATEGORY_OF_SOURCE,
  SOURCE_CLOUD_MAP,
  isOpenRange,
  isInternalExposure,
  isExternalExposure,
  isViaRangeExposure,
  isLoudExposure,
  rangeProvenanceLabel,
  PORT_MATCH_RANK,
  portMatchRank,
  sortPortExposures,
  portRangeLabel,
  getHideInternalPref,
  setHideInternalPref,
  HIDE_INTERNAL_PREF_KEY,
};
